import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, Search, Shield, UserCog, RefreshCw, AlertTriangle } from 'lucide-react';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, userId: null, name: '' });
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const r = await adminApi.getUsers();
      setUsers(r.data);
    } catch (e) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleDelete = async () => {
    try {
      await adminApi.deleteUser(confirmDialog.userId);
      toast.success('Usuario eliminado');
      setConfirmDialog({ open: false, userId: null, name: '' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleStatusChange = async (userId, status) => {
    try {
      await adminApi.updateUser(userId, { status });
      toast.success(`Estado actualizado a ${status}`);
      fetchUsers();
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await adminApi.updateUser(userId, { role, status: 'active' });
      toast.success('Rol actualizado');
      fetchUsers();
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  const handleCleanup = async () => {
    try {
      const r = await adminApi.cleanupTestUsers();
      toast.success(r.data.message);
      fetchUsers();
    } catch (e) {
      toast.error('Error en la limpieza');
    }
  };

  const filtered = users.filter(u => {
    const matchSearch = u.email?.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'pending' && u.status === 'pending') || (filter === 'active' && u.status === 'active') || (filter === 'admin' && u.role === 'admin');
    return matchSearch && matchFilter;
  });

  const statusBadge = (s) => {
    if (s === 'active') return <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Activo</span>;
    if (s === 'pending') return <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">Pendiente</span>;
    return <span className="px-2 py-0.5 rounded-full bg-zinc-500/20 text-zinc-400 text-xs">{s || 'N/A'}</span>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-users-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><UserCog size={28} /> Administrar Usuarios</h1>
            <p className="text-zinc-400 text-sm mt-1">{users.length} usuarios en total, {users.filter(u => u.status === 'pending').length} pendientes</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchUsers} className="border-white/10 text-zinc-300" data-testid="refresh-users"><RefreshCw size={16} className="mr-1" /> Actualizar</Button>
            <Button variant="outline" onClick={handleCleanup} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="cleanup-test-users"><AlertTriangle size={16} className="mr-1" /> Limpiar Datos de Prueba</Button>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o email..." className="pl-10 bg-[#141414] border-white/10 text-white" data-testid="search-users" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-[#141414] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-zinc-400">Nombre</TableHead>
                <TableHead className="text-zinc-400">Email</TableHead>
                <TableHead className="text-zinc-400">Rol</TableHead>
                <TableHead className="text-zinc-400">Estado</TableHead>
                <TableHead className="text-zinc-400">Codigo</TableHead>
                <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Cargando...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No hay usuarios</TableCell></TableRow>
              ) : filtered.map(u => (
                <TableRow key={u.user_id} className="border-white/5">
                  <TableCell className="text-white font-medium">{u.name || '-'}</TableCell>
                  <TableCell className="text-zinc-300 font-mono text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => handleRoleChange(u.user_id, v)}>
                      <SelectTrigger className="w-32 h-8 bg-transparent border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="production_manager">Gerente</SelectItem>
                        <SelectItem value="accounting">Contabilidad</SelectItem>
                        <SelectItem value="team">Empleado</SelectItem>
                        <SelectItem value="client">Cliente</SelectItem>
                        <SelectItem value="pending">Pendiente</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <button onClick={() => handleStatusChange(u.user_id, u.status === 'active' ? 'pending' : 'active')}>
                      {statusBadge(u.status)}
                    </button>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-orange-400">{u.invite_code || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-red-400 h-8 w-8" onClick={() => setConfirmDialog({ open: true, userId: u.user_id, name: u.name || u.email })} data-testid={`delete-user-${u.user_id}`}><Trash2 size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={confirmDialog.open} onOpenChange={(o) => { if (!o) setConfirmDialog({ open: false, userId: null, name: '' }); }}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Eliminar Usuario</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">Estas seguro de eliminar a <span className="text-white font-medium">{confirmDialog.name}</span>? Esta accion es permanente.</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 border-white/10" onClick={() => setConfirmDialog({ open: false, userId: null, name: '' })}>Cancelar</Button>
            <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={handleDelete} data-testid="confirm-delete-user">Eliminar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminUsersPage;
